import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config/api';
import { createPortal } from 'react-dom';
import { InventorySearchService } from '../services/inventorySearchService';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { checkPermission } from '../utils/permissions';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import AccessDenied from './AccessDenied';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';
import { BarcodeScanner } from './BarcodeScanner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Customer {
  id: string;
  name: string;
  phone: string;
  aadhar?: string;
  address?: string;
}

interface InventoryItem {
  id: number;
  inventory_id: string;
  imei_mac_address: string;
  phone_model: string;
  selling_price: number;
  category: string;
  buying_price: number;
  service_expense: number;
  package_expense: number;
  status: 'ready' | 'reserved' | 'service' | 'sold';
  branch_id: number;
  branch_name?: string;
}

interface CartItem {
  inventory_item: InventoryItem;
  selling_price: number;
  profit: number;
}

interface NewSaleForm {
  customer: {
    name: string;
    phone: string;
    aadhar?: string;
    address?: string;
  };
  cart_items: CartItem[];
  total_amount: number;
  final_amount: number;
  total_profit: number;
  payment_method: string;
  branch_id: string;
  sale_date: string;
  notes?: string;
}

interface SaleItem {
  id: number;
  inventory_item_id: number;
  selling_price: number;
  profit: number;
  inventory: {
    id: number;
    inventory_id: string;
    category: string;
    phone_model: string;
    imei_mac_address: string;
    buying_price: number;
    service_expense: number;
    package_expense: number;
  };
}

interface SaleRecord {
  id: string;
  invoiceId: string;
  date: string;
  customer: Customer;
  deviceModel: string;
  imei: string;
  category: string;
  sellingCost: number;
  buyingCost: number;
  expenses: number;
  profit: number;
  profitMargin: number;
  paymentMethod: string;
  branch: string;
  status: 'Completed' | 'Pending' | 'Cancelled';
  notes?: string;
  saleItems?: SaleItem[];
}

const SalesManagement: React.FC = () => {
  const { user, token } = useAuth();
  const { selectedBranchId, selectedBranchName, isAllBranches } = useBranch();
  
  

  // Toast helper function
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('All');
  const [dateRange, setDateRange] = useState('All Time');
  const [selectedSales, setSelectedSales] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isNewSaleModalOpen, setIsNewSaleModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<SaleRecord | null>(null);
  const [salesData, setSalesData] = useState<SaleRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [salesSummary, setSalesSummary] = useState({
    totalCount: 0,
    totalRevenue: 0,
    totalProfit: 0
  });
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Dropdown states
  const [isPaymentMethodDropdownOpen, setIsPaymentMethodDropdownOpen] = useState(false);
  const [isDateRangeDropdownOpen, setIsDateRangeDropdownOpen] = useState(false);

  // Custom date range states
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  
  // Modal dropdown states
  const [isModalPaymentMethodDropdownOpen, setIsModalPaymentMethodDropdownOpen] = useState(false);
  const [isModalBranchDropdownOpen, setIsModalBranchDropdownOpen] = useState(false);
  
  // Modal form states
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<InventoryItem[]>([]);
  const [availableInventory, setAvailableInventory] = useState<InventoryItem[]>([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  
  // Barcode scanner states - automatically active when modal is open
  const [lastScannedValue, setLastScannedValue] = useState<string>('');
  
  // Branches state for modal dropdown
  const [branches, setBranches] = useState<Array<{id: string, name: string}>>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  
  // Toast states
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });
  const [newSaleForm, setNewSaleForm] = useState<NewSaleForm>({
    customer: {
      name: '',
      phone: '',
      aadhar: '',
      address: ''
    },
    cart_items: [],
    total_amount: 0,
    final_amount: 0,
    total_profit: 0,
    payment_method: 'cash',
    branch_id: '',
    sale_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Search function - creates a separate search request with current searchTerm
  const handleSearch = async () => {
    if (!token) return;
    
    setCurrentPage(1);
    setIsLoadingSales(true);
    try {
      // Build query parameters including current searchTerm
      const params = new URLSearchParams();
      params.append('page', '1'); // Reset to page 1 for search
      params.append('limit', itemsPerPage.toString());
      if (searchTerm) params.append('search', searchTerm);
      if (paymentMethodFilter !== 'All') params.append('payment_method', paymentMethodFilter.toLowerCase().replace(' ', '_'));
      
      // Add date filtering (same logic as inventory module)
      if (dateRange !== 'All Time') {
        if (isCustomDateRange && customStartDate && customEndDate) {
          params.append('start_date', customStartDate);
          params.append('end_date', customEndDate);
        } else {
          params.append('date_range', dateRange.toLowerCase().replace(' ', '_'));
        }
      }
      if (!isAllBranches && selectedBranchId) {
        params.append('branch_id', selectedBranchId);
        console.log('🔍 [SALES] Branch filtering applied:', { isAllBranches, selectedBranchId, selectedBranchName });
      } else {
        console.log('🌐 [SALES] Showing all branches:', { isAllBranches, selectedBranchId, selectedBranchName });
      }
      
      console.log('📡 [SALES] API call:', getApiUrl(`sales?${params.toString()}`));
      const response = await fetch(getApiUrl(`sales?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 DEBUG: API Response sale_items:', result.success ? result.sales?.[0]?.sale_items : 'No success');
        if (result.success) {
          // Transform database records to match SaleRecord interface
          const transformedData: SaleRecord[] = result.sales.map((sale: any) => ({
            id: sale.id.toString(),
            invoiceId: sale.invoice_id,
            date: sale.sale_date,
            customer: {
              id: sale.customers?.id?.toString() || '',
              name: sale.customers?.name || '',
              phone: sale.customers?.phone_number || '',
              aadhar: sale.customers?.aadhar || '',
              address: sale.customers?.address || ''
            },
            deviceModel: (sale.sale_items?.length > 1) ? 'Multiple Items' : (sale.sale_items?.[0]?.inventory?.phone_model || 'N/A'),
            imei: (sale.sale_items?.length > 1) ? 'Multiple Items' : (sale.sale_items?.[0]?.inventory?.imei_mac_address || 'N/A'),
            category: (sale.sale_items?.length > 1) ? 'Mixed' : (sale.sale_items?.[0]?.inventory?.category?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'N/A'),
            sellingCost: parseFloat(sale.final_amount || '0'),
            buyingCost: sale.sale_items?.reduce((sum: number, item: any) => sum + parseFloat(item.inventory?.buying_price || '0'), 0) || 0,
            expenses: sale.sale_items?.reduce((sum: number, item: any) => sum + parseFloat(item.inventory?.service_expense || '0') + parseFloat(item.inventory?.package_expense || '0'), 0) || 0,
            profit: parseFloat(sale.total_profit || '0'),
            profitMargin: parseFloat(sale.final_amount || '0') > 0 ? (parseFloat(sale.total_profit || '0') / parseFloat(sale.final_amount || '0')) * 100 : 0,
            paymentMethod: sale.payment_method?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Cash',
            branch: sale.branches?.branch_name || 'Unknown',
            status: 'Completed' as const,
            notes: sale.notes || '',
            saleItems: sale.sale_items || []
          }));

          setSalesData(transformedData);
          setTotalCount(result.pagination?.total || transformedData.length);
          if (result.summary) {
            setSalesSummary(result.summary);
          }
        } else {
          showToast('Failed to fetch sales records', 'error');
        }
      } else {
        showToast('Failed to fetch sales records', 'error');
      }
    } catch (error) {
      showToast('Error fetching sales records', 'error');
    } finally {
      setIsLoadingSales(false);
    }
  };

  // Fetch sales records from database (without search term - search only on button click)
  const fetchSalesRecords = useCallback(async () => {
    if (!token) return;
    
    setIsLoadingSales(true);
    try {
      // Build query parameters (excluding searchTerm - only use in handleSearch)
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());
      if (paymentMethodFilter !== 'All') params.append('payment_method', paymentMethodFilter.toLowerCase().replace(' ', '_'));
      
      // Add date filtering (same logic as inventory module)
      if (dateRange !== 'All Time') {
        if (isCustomDateRange && customStartDate && customEndDate) {
          params.append('start_date', customStartDate);
          params.append('end_date', customEndDate);
        } else {
          params.append('date_range', dateRange.toLowerCase().replace(' ', '_'));
        }
      }
      if (!isAllBranches && selectedBranchId) {
        params.append('branch_id', selectedBranchId);
        console.log('🔍 [SALES] Branch filtering applied:', { isAllBranches, selectedBranchId, selectedBranchName });
      } else {
        console.log('🌐 [SALES] Showing all branches:', { isAllBranches, selectedBranchId, selectedBranchName });
      }
      
      console.log('📡 [SALES] API call:', getApiUrl(`sales?${params.toString()}`));
      const response = await fetch(getApiUrl(`sales?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 DEBUG: API Response sale_items:', result.success ? result.sales?.[0]?.sale_items : 'No success');
        if (result.success) {
          // Transform database records to match SaleRecord interface
          const transformedData: SaleRecord[] = result.sales.map((sale: any) => ({
            id: sale.id.toString(),
            invoiceId: sale.invoice_id,
            date: sale.sale_date,
            customer: {
              id: sale.customers?.id?.toString() || '',
              name: sale.customers?.name || '',
              phone: sale.customers?.phone_number || '',
              aadhar: sale.customers?.aadhar || '',
              address: sale.customers?.address || ''
            },
            deviceModel: (sale.sale_items?.length > 1) ? 'Multiple Items' : (sale.sale_items?.[0]?.inventory?.phone_model || 'N/A'),
            imei: (sale.sale_items?.length > 1) ? 'Multiple Items' : (sale.sale_items?.[0]?.inventory?.imei_mac_address || 'N/A'),
            category: (sale.sale_items?.length > 1) ? 'Mixed' : (sale.sale_items?.[0]?.inventory?.category?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'N/A'),
            sellingCost: parseFloat(sale.final_amount || '0'),
            buyingCost: sale.sale_items?.reduce((sum: number, item: any) => sum + parseFloat(item.inventory?.buying_price || '0'), 0) || 0,
            expenses: sale.sale_items?.reduce((sum: number, item: any) => sum + parseFloat(item.inventory?.service_expense || '0') + parseFloat(item.inventory?.package_expense || '0'), 0) || 0,
            profit: parseFloat(sale.total_profit || '0'),
            profitMargin: parseFloat(sale.final_amount || '0') > 0 ? (parseFloat(sale.total_profit || '0') / parseFloat(sale.final_amount || '0')) * 100 : 0,
            paymentMethod: sale.payment_method?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Cash',
            branch: sale.branches?.branch_name || 'Unknown',
            status: 'Completed' as const,
            notes: sale.notes || '',
            saleItems: sale.sale_items || []
          }));

          setSalesData(transformedData);
          setTotalCount(result.pagination?.total || transformedData.length);
          if (result.summary) {
            setSalesSummary(result.summary);
          }
        } else {
          showToast('Failed to fetch sales records', 'error');
        }
      } else {
        showToast('Failed to fetch sales records', 'error');
      }
    } catch (error) {
      showToast('Error fetching sales records', 'error');
    } finally {
      setIsLoadingSales(false);
    }
  }, [token, paymentMethodFilter, dateRange, selectedBranchId, isAllBranches, currentPage, itemsPerPage, isCustomDateRange, customStartDate, customEndDate]);

  // Fetch available inventory from API
  const fetchAvailableInventory = async () => {
    if (!token) return;
    
    try {
      setIsLoadingInventory(true);
      const params = new URLSearchParams();
      params.append('status', 'ready'); // Only fetch ready items
      params.append('limit', '100'); // Get more items for search
      
      // Apply branch filtering for branch managers
      if (user?.role === 'branch_manager') {
        if (!isAllBranches && selectedBranchId) {
          params.append('branch_id', selectedBranchId);
        }
      } else {
        // For admins and inventory managers, respect current branch selection
        if (!isAllBranches && selectedBranchId) {
          params.append('branch_id', selectedBranchId);
        }
      }
      
      const response = await fetch(`${getApiUrl(`inventory?${params.toString()}`)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const transformedData = data.inventory.map((item: any) => ({
            ...item,
            branch_name: item.branches?.branch_name || 'Unknown Branch'
          }));
          setAvailableInventory(transformedData);
        }
      }
    } catch (error) {
    } finally {
      setIsLoadingInventory(false);
    }
  };

  // Helper function for inventory search that can be used by both manual and barcode
  const performInventorySearch = async (searchTerm: string) => {
    const trimmedSearchTerm = searchTerm.trim();
    if (!token || !trimmedSearchTerm) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    try {
      setIsLoadingInventory(true);
      setHasSearched(true);
      
      const result = await InventorySearchService.getAvailableInventory(token, trimmedSearchTerm, {
        branchId: selectedBranchId || undefined,
        excludeCartItems: cartItems,
        userRole: user?.role,
        isAllBranches
      });

      if (result.success) {
        // Transform data to include branch_name for backward compatibility
        const transformedData = result.items.map((item: any) => ({
          ...item,
          branch_name: item.branches?.branch_name || 'Unknown Branch'
        }));
        setSearchResults(transformedData);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching inventory:', error);
      setSearchResults([]);
    } finally {
      setIsLoadingInventory(false);
    }
  };

  // Manual search function
  const handleInventorySearch = async () => {
    await performInventorySearch(inventorySearchTerm);
  };

  // Barcode scan handler - just sets the search term and triggers the same search logic
  const handleBarcodeScan = async (scannedValue: string) => {
    console.log('🔍 Barcode scanned in SalesManagement:', scannedValue);
    
    // Prevent duplicate scans
    if (scannedValue === lastScannedValue) {
      return;
    }
    
    // Set the scanned value and track it
    setInventorySearchTerm(scannedValue);
    setLastScannedValue(scannedValue);
    
    // Use the same search logic as manual search
    await performInventorySearch(scannedValue);
  };

  // Close all filter dropdowns
  const closeAllDropdowns = useCallback(() => {
    setIsPaymentMethodDropdownOpen(false);
    setIsDateRangeDropdownOpen(false);
  }, []);

  // Fetch company information
  const fetchCompanyInfo = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl("companies"), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.company) {
          setCompanyInfo(data.company);
        }
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  }, [token]);

  // Fetch branches from database
  const fetchBranches = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl("branches"), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.branches) {
          // Transform branches data for modal dropdown
          const branchOptions = data.branches.map((branch: any) => ({
            id: branch.id,
            name: branch.branch_name
          }));
          setBranches(branchOptions);
        }
      } else {
        console.error('Failed to fetch branches:', response.status);
        // Fallback to default branches if API fails
        setBranches([
          { id: '1', name: 'Main Branch - Mumbai' },
          { id: '2', name: 'Branch 2 - Delhi' },
          { id: '3', name: 'Branch 3 - Bangalore' },
          { id: '4', name: 'Branch 4 - Chennai' },
          { id: '5', name: 'Branch 5 - Kolkata' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      // Fallback to default branches if fetch fails
      setBranches([
        { id: '1', name: 'Main Branch - Mumbai' },
        { id: '2', name: 'Branch 2 - Delhi' },
        { id: '3', name: 'Branch 3 - Bangalore' },
        { id: '4', name: 'Branch 4 - Chennai' },
        { id: '5', name: 'Branch 5 - Kolkata' }
      ]);
    }
  }, [token]);

  // Load sales records when component mounts
  useEffect(() => {
    if (token) {
      fetchSalesRecords();
      fetchCompanyInfo();
      fetchBranches();
    }
  }, [token, fetchSalesRecords, fetchCompanyInfo, fetchBranches]);

  // Refetch sales when filters change (like inventory module)
  useEffect(() => {
    if (token && user) {
      fetchSalesRecords(); // Reset to current page when filters change
    }
  }, [token, user, paymentMethodFilter, selectedBranchId, isAllBranches, dateRange, isCustomDateRange, customStartDate, customEndDate]);

  // Load inventory when component mounts or modal opens  
  useEffect(() => {
    if (token && isNewSaleModalOpen) {
      fetchAvailableInventory();
    }
  }, [token, isNewSaleModalOpen, selectedBranchId, isAllBranches, user?.role]);
  
  // Also refresh inventory when branch selection changes for open modal
  useEffect(() => {
    if (isNewSaleModalOpen && token) {
      fetchAvailableInventory();
    }
  }, [selectedBranchId, isAllBranches]);

  // Auto-set branch for branch managers when modal opens
  useEffect(() => {
    if (isNewSaleModalOpen && user?.role === 'branch_manager' && selectedBranchId) {
      setNewSaleForm(prev => ({ ...prev, branch_id: selectedBranchId }));
    }
  }, [isNewSaleModalOpen, user?.role, selectedBranchId]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeAllDropdowns]);


  // Check permissions
  if (!checkPermission(user?.role, 'sales')) {
    return <AccessDenied resource="Sales Management" />;
  }

  // Dropdown options
  const paymentMethodOptions = [
    'All',
    'Cash',
    'Bank Transfer',
    'UPI'
  ];

  const dateRangeOptions = [
    'All Time',
    'This Month',
    'Last Month',
    'This Year',
    'Last Year',
    'Custom'
  ];
  
  // Modal dropdown options
  const modalPaymentMethodOptions = ['Cash', 'UPI', 'Bank Transfer', 'Credit Card', 'Debit Card'];
  
  // Dynamic branch options based on user role
  const getAvailableBranches = () => {
    if (user?.role === 'branch_manager') {
      // Branch managers can only access their assigned branch
      if (selectedBranchName !== 'All Branches') {
        // Find the branch in the fetched branches list
        const userBranch = branches.find(branch => branch.name === selectedBranchName);
        return userBranch ? [userBranch.name] : [selectedBranchName];
      }
      return [];
    } else {
      // Admins and inventory managers can access all branches (excluding "All Branches" option)
      return branches.map(branch => branch.name);
    }
  };
  
  const modalBranchOptions = getAvailableBranches();
  
  // Helper function to get branch name by ID for display
  const getBranchNameById = (branchId: string) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : branchId;
  };
  

  // Cart management functions
  const addToCart = (inventoryItem: InventoryItem, sellingPrice: number) => {
    const totalCost = (typeof inventoryItem.buying_price === 'string' ? parseFloat(inventoryItem.buying_price) : inventoryItem.buying_price) + (typeof inventoryItem.service_expense === 'string' ? parseFloat(inventoryItem.service_expense) : inventoryItem.service_expense) + (typeof inventoryItem.package_expense === 'string' ? parseFloat(inventoryItem.package_expense) : inventoryItem.package_expense);
    const profit = sellingPrice - totalCost;
    
    const cartItem: CartItem = {
      inventory_item: inventoryItem,
      selling_price: sellingPrice,
      profit: profit
    };
    
    setCartItems(prev => [...prev, cartItem]);
    updateSaleTotals([...cartItems, cartItem]);
    setSearchResults([]);
    setInventorySearchTerm('');
    setHasSearched(false);
  };

  const removeFromCart = (itemId: number) => {
    const updatedCart = cartItems.filter(item => item.inventory_item.id !== itemId);
    setCartItems(updatedCart);
    updateSaleTotals(updatedCart);
  };

  const updateCartItemPrice = (itemId: number, newPrice: number) => {
    const updatedCart = cartItems.map(item => {
      if (item.inventory_item.id === itemId) {
        const totalCost = (typeof item.inventory_item.buying_price === 'string' ? parseFloat(item.inventory_item.buying_price) : item.inventory_item.buying_price) + (typeof item.inventory_item.service_expense === 'string' ? parseFloat(item.inventory_item.service_expense) : item.inventory_item.service_expense) + (typeof item.inventory_item.package_expense === 'string' ? parseFloat(item.inventory_item.package_expense) : item.inventory_item.package_expense);
        return {
          ...item,
          selling_price: newPrice,
          profit: newPrice - totalCost
        };
      }
      return item;
    });
    setCartItems(updatedCart);
    updateSaleTotals(updatedCart);
  };

  const updateSaleTotals = (items: CartItem[]) => {
    const totalAmount = items.reduce((sum, item) => sum + item.selling_price, 0);
    const totalProfit = items.reduce((sum, item) => sum + item.profit, 0);
    const finalAmount = totalAmount;

    setNewSaleForm(prev => ({
      ...prev,
      cart_items: items,
      total_amount: totalAmount,
      final_amount: finalAmount,
      total_profit: totalProfit
    }));
  };

  // Generate order number
  const generateOrderNumber = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const date = String(today.getDate()).padStart(2, '0');
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `IH-ORD${year}${month}${date}${randomNum}`;
  };

  // Generate invoice ID
  const generateInvoiceId = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}-${randomNum}`;
  };

  // Lookup customer by phone number
  const lookupCustomerByPhone = async (phoneNumber: string) => {
    // Extract digits only for length check
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    if (!token || digitsOnly.length < 10) return;
    
    try {
      setIsLoadingCustomer(true);
      const response = await fetch(getApiUrl(`customers/search?phone=${encodeURIComponent(phoneNumber)}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.customer) {
          setNewSaleForm(prev => ({
            ...prev,
            customer: {
              name: data.customer.name || '',
              phone: data.customer.phone_number || phoneNumber,
              aadhar: data.customer.aadhar || '',
              address: data.customer.address || ''
            }
          }));
        }
      }
    } catch (error) {
      console.error('Customer lookup error:', error);
    } finally {
      setIsLoadingCustomer(false);
    }
  };

  // Handle form submission
  const handleSaleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      showToast('Please add at least one item to the cart', 'warning');
      return;
    }
    
    if (!newSaleForm.customer.name || !newSaleForm.customer.phone) {
      showToast('Please fill in customer name and phone number', 'warning');
      return;
    }

    if (!newSaleForm.branch_id) {
      showToast('Please select a branch', 'warning');
      return;
    }
    
    // Additional validation for branch managers
    if (user?.role === 'branch_manager') {
      if (selectedBranchName === 'All Branches' || !selectedBranchId) {
        showToast('Branch managers must select their assigned branch', 'error');
        return;
      }
      // Check if the selected branch matches the user's assigned branch
      if (newSaleForm.branch_id !== selectedBranchId) {
        showToast('Branch managers can only create sales for their assigned branch', 'error');
        return;
      }
    }

    try {
      const saleData = {
        customer: newSaleForm.customer,
        cart_items: cartItems,
        total_amount: newSaleForm.total_amount,
        final_amount: newSaleForm.final_amount,
        total_profit: newSaleForm.total_profit,
        payment_method: newSaleForm.payment_method,
        branch_id: newSaleForm.branch_id,
        sale_date: newSaleForm.sale_date,
        notes: newSaleForm.notes
      };

      const response = await fetch(getApiUrl("sales"), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showToast(`Sale created successfully! Invoice ID: ${result.sale.invoice_id}`, 'success');
        
        // Reset form and close modal
        resetForm();
        setIsNewSaleModalOpen(false);
        
        // Refresh sales data
        fetchSalesRecords();
      } else {
        showToast(result.error || 'Failed to create sale. Please try again.', 'error');
      }
      
    } catch (error) {
      showToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  // Helper function to convert image to base64
  const getLogoAsBase64 = async (): Promise<string> => {
    try {
      const response = await fetch('/images/gadgetly-logo.jpg');
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading logo:', error);
      // Return a placeholder or empty string if logo fails to load
      return '';
    }
  };

  // Download Invoice Function - Generate PDF
  const downloadInvoice = async (saleData?: SaleRecord) => {
    const saleToUse = saleData || selectedSale;
    
    if (!saleToUse) {
      showToast('No sale selected for download', 'error');
      return;
    }

    // If company info is not loaded, try to fetch it first
    if (!companyInfo) {
      showToast('Loading company information...', 'info');
      await fetchCompanyInfo();
      
      // Double check after fetch attempt
      if (!companyInfo) {
        showToast('Company information not available. Please contact administrator.', 'error');
        return;
      }
    }

    // Convert logo to base64 for embedded HTML
    const logoBase64 = await getLogoAsBase64();

    const invoiceContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${saleToUse.invoiceId}</title>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 14px; 
            color: #333; 
            line-height: 1.6; 
            background: white;
          }
          .invoice-container { 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 40px; 
            background: white;
          }
          
          .header { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-start; 
            margin-bottom: 40px; 
            border-bottom: 3px solid #000; 
            padding-bottom: 30px; 
          }
          
          .company-section { flex: 1; }
          .company-logo { 
            width: 120px; 
            height: 60px; 
            object-fit: contain; 
            margin-bottom: 15px; 
          }
          .company-name { 
            font-size: 28px; 
            font-weight: bold; 
            color: #000; 
            margin-bottom: 10px;
            text-transform: uppercase;
          }
          .company-details { 
            font-size: 14px; 
            color: #555; 
            line-height: 1.4;
          }
          
          .invoice-section { 
            text-align: right; 
            flex: 1; 
          }
          .invoice-title { 
            font-size: 36px; 
            font-weight: bold; 
            color: #000; 
            margin-bottom: 15px;
            text-transform: uppercase;
          }
          .invoice-details { 
            font-size: 14px; 
            background: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            border: 1px solid #dee2e6;
          }
          .invoice-details div { margin-bottom: 5px; }
          
          .billing-section { 
            display: flex; 
            justify-content: space-between; 
            margin: 30px 0; 
            background: #f8f9fa;
            padding: 20px;
            border-radius: 5px;
          }
          .bill-to, .sale-info { 
            flex: 1; 
            margin-right: 30px; 
          }
          .section-header { 
            font-size: 16px; 
            font-weight: bold; 
            margin-bottom: 15px; 
            color: #000; 
            text-transform: uppercase;
            border-bottom: 2px solid #000;
            padding-bottom: 5px;
          }
          .detail-row { 
            margin-bottom: 8px; 
            display: flex;
            align-items: flex-start;
          }
          .detail-label { 
            font-weight: bold; 
            min-width: 120px; 
            color: #000;
          }
          .detail-value { 
            flex: 1; 
            color: #333;
          }
          
          .items-section { 
            margin: 30px 0; 
          }
          .items-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 15px;
            border: 1px solid #dee2e6;
          }
          .items-table th { 
            background: #000; 
            color: white; 
            padding: 15px 10px; 
            text-align: left; 
            font-weight: bold;
            font-size: 14px;
            text-transform: uppercase;
          }
          .items-table td { 
            padding: 15px 10px; 
            border-bottom: 1px solid #dee2e6;
            vertical-align: top;
          }
          .items-table tr:nth-child(even) {
            background: #f8f9fa;
          }
          .items-table tr:hover {
            background: #e9ecef;
          }
          
          .summary-section { 
            margin-top: 30px; 
            display: flex; 
            justify-content: flex-end; 
          }
          .summary-table { 
            width: 350px; 
            border: 1px solid #dee2e6;
            border-radius: 5px;
            overflow: hidden;
          }
          .summary-table td { 
            padding: 12px 15px; 
            border-bottom: 1px solid #dee2e6;
          }
          .summary-table .label-col { 
            font-weight: bold; 
            background: #f8f9fa;
            color: #000;
          }
          .summary-table .amount-col { 
            text-align: right; 
            font-weight: bold;
            width: 150px;
          }
          .summary-table .total-row { 
            background: #000; 
            color: white; 
            font-size: 16px; 
            font-weight: bold;
          }
          .summary-table .total-row td {
            border-bottom: none;
          }
          
          .notes-section { 
            margin: 30px 0; 
            padding: 20px;
            background: #f8f9fa;
            border-radius: 5px;
            border-left: 4px solid #000;
          }
          
          .footer-section { 
            margin-top: 40px; 
            text-align: center; 
            padding: 20px 0; 
            border-top: 2px solid #000;
            color: #666;
          }
          .footer-section .thank-you {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #000;
          }
          
          .company-logo-placeholder {
            width: 120px; 
            height: 60px; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            border: 2px solid #000; 
            border-radius: 8px; 
            font-weight: bold; 
            color: #000; 
            font-size: 14px; 
            text-align: center;
            margin-bottom: 15px;
            background: #f8f9fa;
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <!-- Header Section -->
          <div class="header">
            <div class="company-section">
              ${logoBase64 ? `<img src="${logoBase64}" alt="Company Logo" class="company-logo">` : '<div class="company-logo-placeholder">iHub Pulse</div>'}
              <div class="company-name">${companyInfo.company_name || 'iHub Pulse'}</div>
              <div class="company-details">
                ${companyInfo.address ? `<div><strong>Address:</strong> ${companyInfo.address}</div>` : ''}
                ${companyInfo.phone_number ? `<div><strong>Phone:</strong> ${companyInfo.phone_number}</div>` : ''}
                ${companyInfo.email_address ? `<div><strong>Email:</strong> ${companyInfo.email_address}</div>` : ''}
                ${companyInfo.gst_number ? `<div><strong>GST:</strong> ${companyInfo.gst_number}</div>` : ''}
              </div>
            </div>
            <div class="invoice-section">
              <div class="invoice-title">Invoice</div>
              <div class="invoice-details">
                <div><strong>Invoice Number:</strong> ${saleToUse.invoiceId}</div>
                <div><strong>Invoice Date:</strong> ${new Date(saleToUse.date).toLocaleDateString('en-IN', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</div>
                <div><strong>Branch:</strong> ${saleToUse.branch}</div>
              </div>
            </div>
          </div>

          <!-- Billing Information -->
          <div class="billing-section">
            <div class="bill-to">
              <div class="section-header">Bill To</div>
              <div class="detail-row">
                <div class="detail-label">Customer Name:</div>
                <div class="detail-value">${saleToUse.customer.name}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">Phone Number:</div>
                <div class="detail-value">${saleToUse.customer.phone}</div>
              </div>
              ${saleToUse.customer.address ? `
              <div class="detail-row">
                <div class="detail-label">Address:</div>
                <div class="detail-value">${saleToUse.customer.address}</div>
              </div>
              ` : ''}
            </div>
            <div class="sale-info">
              <div class="section-header">Sale Information</div>
              <div class="detail-row">
                <div class="detail-label">Payment Method:</div>
                <div class="detail-value">${saleToUse.paymentMethod}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">Sale Status:</div>
                <div class="detail-value">${saleToUse.status}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">Sale Date:</div>
                <div class="detail-value">${new Date(saleToUse.date).toLocaleDateString('en-IN')}</div>
              </div>
            </div>
          </div>

          <!-- Items Section -->
          <div class="items-section">
            <div class="section-header">Product Details</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width: 35%;">Product Description</th>
                  <th style="width: 20%;">Category</th>
                  <th style="width: 25%;">IMEI/MAC Address</th>
                  <th style="width: 20%; text-align: right;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${saleToUse.saleItems && saleToUse.saleItems.length > 1 ? 
                  saleToUse.saleItems.map(item => `
                    <tr>
                      <td><strong>${item.inventory.phone_model}</strong></td>
                      <td>${item.inventory.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                      <td><code>${item.inventory.imei_mac_address}</code></td>
                      <td style="text-align: right;"><strong>${formatCurrency(item.selling_price)}</strong></td>
                    </tr>
                  `).join('') :
                  `<tr>
                    <td><strong>${saleToUse.deviceModel}</strong></td>
                    <td>${saleToUse.category}</td>
                    <td><code>${saleToUse.imei}</code></td>
                    <td style="text-align: right;"><strong>${formatCurrency(saleToUse.sellingCost)}</strong></td>
                  </tr>`
                }
              </tbody>
            </table>
          </div>

          <!-- Summary Section -->
          <div class="summary-section">
            <table class="summary-table">
              <tr>
                <td class="label-col">Subtotal:</td>
                <td class="amount-col">${formatCurrency(saleToUse.sellingCost)}</td>
              </tr>
              <tr>
                <td class="label-col">Tax (0%):</td>
                <td class="amount-col">${formatCurrency(0)}</td>
              </tr>
              <tr>
                <td class="label-col">Discount:</td>
                <td class="amount-col">${formatCurrency(0)}</td>
              </tr>
              <tr class="total-row">
                <td>Total Amount:</td>
                <td>${formatCurrency(saleToUse.sellingCost)}</td>
              </tr>
            </table>
          </div>

          ${saleToUse.notes ? `
          <!-- Notes Section -->
          <div class="notes-section">
            <div class="section-header">Additional Notes</div>
            <div>${saleToUse.notes}</div>
          </div>
          ` : ''}

          <!-- Footer Section -->
          <div class="footer-section">
            <div class="thank-you">Thank you for your business!</div>
            <div>This invoice was generated electronically and is valid without signature.</div>
            <div style="margin-top: 10px; font-size: 12px;">
              Generated on ${new Date().toLocaleDateString('en-IN', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    // Create PDF from HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = invoiceContent;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);

    const canvas = await html2canvas(tempDiv.querySelector('.invoice-container')!, {
      useCORS: true
    });

    document.body.removeChild(tempDiv);

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`invoice-${saleToUse.invoiceId}.pdf`);
    
    showToast('PDF downloaded successfully!', 'success');
  };

  // Export CSV function
  const exportToCSV = async () => {
    try {
      setIsExporting(true);
      
      // Build the same parameters used for fetching data
      const params = new URLSearchParams();
      
      // Search Term
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Payment Method Filter
      if (paymentMethodFilter !== 'All') {
        params.append('payment_method', paymentMethodFilter.toLowerCase().replace(' ', '_'));
      }
      
      // Date Range Filter
      if (dateRange === 'Custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      } else if (dateRange && dateRange !== 'All Time') {
        params.append('date_range', dateRange.toLowerCase().replace(' ', '_'));
      }
      
      // Branch Selection
      if (!isAllBranches && selectedBranchId) {
        params.append('branch_id', selectedBranchId);
      }
      
      // Export Flag
      params.append('export', 'true');
      params.append('format', 'csv');
      
      try {
        // Try server-side export first
        const response = await fetch(getApiUrl(`sales/export?${params.toString()}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          // Server-side export successful
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          
          // Generate filename with filters
          const currentDate = new Date().toISOString().split('T')[0];
          let filename = `sales-export-${currentDate}`;
          
          if (dateRange === 'Custom' && customStartDate && customEndDate) {
            filename += `-${customStartDate}-to-${customEndDate}`;
          } else if (dateRange !== 'Today') {
            filename += `-${dateRange.toLowerCase().replace(' ', '-')}`;
          }
          if (paymentMethodFilter !== 'All') filename += `-${paymentMethodFilter.toLowerCase().replace(' ', '-')}`;
          if (!isAllBranches && selectedBranchName !== 'All Branches') {
            filename += `-${selectedBranchName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
          }
          if (searchTerm) filename += '-filtered';
          
          // Download file
          const link = document.createElement('a');
          link.href = url;
          link.download = `${filename}.csv`;
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          showToast('Sales exported successfully!', 'success');
          return;
        }
      } catch (serverError) {
        console.warn('Server-side export failed, falling back to client-side export:', serverError);
      }
      
      // Fallback to client-side export if server-side fails
      console.log('Using client-side export fallback');
      
      if (salesData.length === 0) {
        showToast('No data to export', 'warning');
        return;
      }

      // Define CSV headers
      const headers = [
        'Invoice ID',
        'Date',
        'Customer Name',
        'Customer Phone',
        'Customer Address',
        'Device Model',
        'Category',
        'IMEI/MAC',
        `Sale Amount (${getCurrencySymbol()})`,
        `Buying Cost (${getCurrencySymbol()})`,
        `Expenses (${getCurrencySymbol()})`,
        `Profit (${getCurrencySymbol()})`,
        'Profit Margin (%)',
        'Payment Method',
        'Branch',
        'Status',
        'Notes'
      ];

      // Convert data to CSV format
      const csvData = salesData.map((sale: SaleRecord) => [
        sale.invoiceId,
        new Date(sale.date).toLocaleDateString('en-IN'),
        sale.customer.name,
        sale.customer.phone,
        sale.customer.address || '',
        sale.deviceModel,
        sale.category,
        sale.imei,
        sale.sellingCost,
        sale.buyingCost,
        sale.expenses,
        sale.profit,
        sale.profitMargin.toFixed(2),
        sale.paymentMethod,
        sale.branch,
        sale.status,
        sale.notes || ''
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          row.map(field => {
            // Handle fields that contain commas, quotes, or line breaks
            const stringField = String(field);
            if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
              return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
          }).join(',')
        )
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Generate filename with current date and filters
        const currentDate = new Date().toISOString().split('T')[0];
        let filename = `sales-export-${currentDate}`;
        
        // Add filter info to filename
        if (dateRange === 'Custom' && customStartDate && customEndDate) {
          filename += `-${customStartDate}-to-${customEndDate}`;
        } else if (dateRange !== 'Today') {
          filename += `-${dateRange.toLowerCase().replace(' ', '-')}`;
        }
        if (paymentMethodFilter !== 'All') {
          filename += `-${paymentMethodFilter.toLowerCase().replace(' ', '-')}`;
        }
        if (!isAllBranches && selectedBranchName !== 'All Branches') {
          filename += `-${selectedBranchName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
        }
        if (searchTerm) {
          filename += '-filtered';
        }
        
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showToast(`Successfully exported ${salesData.length} sales records (current page data)`, 'success');
      }
      
    } catch (error) {
      console.error('Export error:', error);
      showToast('Failed to export sales data', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCartItems([]);
    
    // Auto-set branch for branch managers, leave empty for admins
    const defaultBranchId = user?.role === 'branch_manager' && selectedBranchName !== 'All Branches' 
      ? selectedBranchName 
      : '';
    
    setNewSaleForm({
      customer: {
        name: '',
        phone: '',
        aadhar: '',
        address: ''
      },
      cart_items: [],
      total_amount: 0,
      final_amount: 0,
      total_profit: 0,
      payment_method: 'cash',
      branch_id: defaultBranchId,
      sale_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setSearchResults([]);
    setInventorySearchTerm('');
    setHasSearched(false);
    
    // Reset scanner state
    setLastScannedValue('');
    
    // Refresh inventory when modal opens (to get branch-filtered results)
    if (user?.role === 'branch_manager' || !isAllBranches) {
      fetchAvailableInventory();
    }
  };


  // Function to generate dynamic titles based on selected date range
  const getDynamicTitles = (selectedRange: string) => {
    switch (selectedRange) {
      case 'All Time':
        return {
          salesTitle: "All Time Sales",
          totalTitle: "All Time Revenue", 
          profitTitle: "All Time Profit",
          averageTitle: "All Time Average"
        };
      case 'This Year':
        return {
          salesTitle: "This Year's Sales",
          totalTitle: "This Year's Revenue",
          profitTitle: "This Year's Profit",
          averageTitle: "This Year's Average"
        };
      case 'Last Year':
        return {
          salesTitle: "Last Year's Sales", 
          totalTitle: "Last Year's Revenue",
          profitTitle: "Last Year's Profit",
          averageTitle: "Last Year's Average"
        };
      case 'This Month':
        return {
          salesTitle: "This Month's Sales",
          totalTitle: "This Month's Revenue", 
          profitTitle: "This Month's Profit",
          averageTitle: "This Month's Average"
        };
      case 'Last Month':
        return {
          salesTitle: "Last Month's Sales",
          totalTitle: "Last Month's Revenue",
          profitTitle: "Last Month's Profit", 
          averageTitle: "Last Month's Average"
        };
      case 'Custom':
        const startDateFormatted = customStartDate ? new Date(customStartDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
        const endDateFormatted = customEndDate ? new Date(customEndDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
        const dateRangeText = startDateFormatted && endDateFormatted ? `${startDateFormatted} - ${endDateFormatted}` : 'Selected Period';
        
        return {
          salesTitle: `${dateRangeText} Sales`,
          totalTitle: `${dateRangeText} Revenue`,
          profitTitle: `${dateRangeText} Profit`,
          averageTitle: `${dateRangeText} Average`
        };
      default:
        return {
          salesTitle: "Total Sales",
          totalTitle: "Total Revenue", 
          profitTitle: "Total Profit",
          averageTitle: "Average Sale"
        };
    }
  };

  // Get dynamic titles based on current date range selection
  const dynamicTitles = getDynamicTitles(dateRange);

  // Calculate summary data - use summary from API for accurate totals across all sales
  const filteredSalesCount = salesSummary.totalCount;
  const filteredSalesAmount = salesSummary.totalRevenue;
  const filteredProfit = salesSummary.totalProfit;
  const averageSaleValue = salesSummary.totalCount > 0 ? salesSummary.totalRevenue / salesSummary.totalCount : 0;

  // Use salesData directly since filtering is done server-side
  // Server-side pagination - use data directly from API
  const currentData = salesData;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Helper functions
  const getCategoryIcon = (category: string) => {
    const icons = {
      'Mobile Phone': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      'Laptop': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      'Tablet': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      'Accessories': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 2v12a2 2 0 002 2h8a2 2 0 002-2V6M9 10h6" />
        </svg>
      )
    };
    return icons[category as keyof typeof icons] || icons['Mobile Phone'];
  };

  const handleSelectAll = () => {
    if (selectedSales.length === currentData.length) {
      setSelectedSales([]);
    } else {
      setSelectedSales(currentData.map(sale => sale.id));
    }
  };

  const handleSelectSale = (saleId: string) => {
    setSelectedSales(prev => 
      prev.includes(saleId) 
        ? prev.filter(id => id !== saleId)
        : [...prev, saleId]
    );
  };
  
  // Close all modal dropdowns
  const closeAllModalDropdowns = () => {
    setIsModalPaymentMethodDropdownOpen(false);
    setIsModalBranchDropdownOpen(false);
  };

  // Icons
  const SearchIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const FilterIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
    </svg>
  );

  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const ExportIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
  );

  const DownloadIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  return (
    <>
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Sales Management</h1>
          <p className="text-gray-500 mt-1">
            Track and manage all your sales transactions
            {!isAllBranches && selectedBranchId ? (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                📍 Filtering: {selectedBranchName}
              </span>
            ) : (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                🌐 Showing: All Branches
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <button 
            onClick={exportToCSV}
            disabled={isExporting}
            className="w-full sm:w-auto px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 transition-colors h-10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="whitespace-nowrap">Exporting...</span>
              </>
            ) : (
              <>
                <ExportIcon />
                <span className="whitespace-nowrap">Export Sales</span>
              </>
            )}
          </button>
          <button 
            onClick={() => {
              resetForm();
              setIsNewSaleModalOpen(true);
            }}
            className="w-full sm:w-auto px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center justify-center gap-2 transition-colors h-10"
          >
            <PlusIcon />
            <span className="whitespace-nowrap">Add New Sale</span>
          </button>
        </div>
      </div>

      {/* Branch Filter Info */}
      {!isAllBranches && selectedBranchId && user?.role !== 'branch_manager' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Branch Filter Active
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                Showing only sales from <strong>{selectedBranchName}</strong>. 
                Select "All Branches" to see sales from all locations.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{dynamicTitles.salesTitle}</p>
              <p className="text-2xl font-bold text-gray-900">{filteredSalesCount} sales</p>
              <p className="text-sm text-gray-500">{formatCurrency(filteredSalesAmount)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{dynamicTitles.totalTitle}</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(filteredSalesAmount)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{dynamicTitles.profitTitle}</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(filteredProfit)}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{dynamicTitles.averageTitle}</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(Math.round(averageSaleValue))}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search - Left side */}
          <div className="flex items-center gap-3 max-w-md">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Invoice ID, customer phone, IMEI, or model..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                style={{ width: '460px' }}
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors h-10 whitespace-nowrap flex items-center justify-center"
            >
              Search
            </button>
          </div>

          {/* Filters - Right side */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Range Dropdown */}
            <div className="relative dropdown-container">
              <button 
                onClick={() => {
                  closeAllDropdowns();
                  setIsDateRangeDropdownOpen(!isDateRangeDropdownOpen);
                }}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <span className="text-sm font-medium text-gray-700">
                  {dateRange === 'Custom' && isCustomDateRange 
                    ? `${customStartDate} to ${customEndDate}` 
                    : dateRange
                  }
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDateRangeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isDateRangeDropdownOpen && (
                <div className="absolute left-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {dateRangeOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (option === 'Custom') {
                          // Set default custom range if not already set
                          if (!tempStartDate || !tempEndDate) {
                            const today = new Date();
                            const thirtyDaysAgo = new Date();
                            thirtyDaysAgo.setDate(today.getDate() - 30);
                            
                            setTempEndDate(today.toISOString().split('T')[0]);
                            setTempStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                          }
                          setIsCustomDateModalOpen(true);
                        } else {
                          setDateRange(option);
                          setIsCustomDateRange(false);
                          setCurrentPage(1);
                        }
                        setIsDateRangeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        dateRange === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Payment Method Dropdown */}
            <div className="relative dropdown-container">
              <button 
                onClick={() => {
                  closeAllDropdowns();
                  setIsPaymentMethodDropdownOpen(!isPaymentMethodDropdownOpen);
                }}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <span className="text-sm font-medium text-gray-700">
                  {paymentMethodFilter === 'All' ? 'All Payments' : paymentMethodFilter}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isPaymentMethodDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isPaymentMethodDropdownOpen && (
                <div className="absolute left-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {paymentMethodOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setPaymentMethodFilter(option);
                        setIsPaymentMethodDropdownOpen(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        paymentMethodFilter === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option === 'All' ? 'All Payments' : option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button 
              onClick={() => {
                setSearchTerm('');
                setPaymentMethodFilter('All');
                setDateRange('Today');
                setCustomStartDate('');
                setCustomEndDate('');
                setCurrentPage(1);
                closeAllDropdowns();
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors border border-gray-200 rounded-lg hover:border-gray-300 bg-white h-10"
            >
              <FilterIcon />
              Clear Filters
            </button>
          </div>
        </div>
      </div>


      {/* Sales Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoadingSales ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center">
                    <LoadingSpinner />
                    <p className="text-gray-500 mt-2">Loading sales records...</p>
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                    No sales records found
                  </td>
                </tr>
              ) : (
                currentData.map((sale) => (
                <tr key={sale.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(sale.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => {
                        setSelectedSale(sale);
                        setIsInvoiceModalOpen(true);
                      }}
                      className="text-sm font-mono font-medium text-primary-600 hover:text-primary-900"
                    >
                      {sale.invoiceId}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{sale.customer.name}</div>
                      <div className="text-sm text-gray-500">{sale.customer.phone}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{sale.deviceModel}</div>
                      <div className="text-sm text-gray-500 font-mono">
                        IMEI/MAC: {sale.imei}
                        {sale.deviceModel === 'Multiple Items' && (
                          <div className="text-xs text-blue-600 mt-1">Click the view icon →</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="p-1 bg-gray-100 rounded">
                        {getCategoryIcon(sale.category)}
                      </div>
                      <span className="text-sm text-gray-900">{sale.category}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {formatCurrency(sale.sellingCost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-green-600">{formatCurrency(sale.profit)}</div>
                      <div className="text-sm text-gray-500">{sale.profitMargin.toFixed(1)}%</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                      {sale.paymentMethod}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          setSelectedSale(sale);
                          setIsInvoiceModalOpen(true);
                        }}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => downloadInvoice(sale).catch(console.error)}
                        className="text-green-600 hover:text-green-900"
                        title="Download Invoice"
                      >
                        <DownloadIcon />
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-4 sm:mb-0">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">
              entries (Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalCount)} of {totalCount})
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              const page = currentPage <= 3 ? index + 1 : currentPage - 2 + index;
              if (page > totalPages) return null;
              
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    page === currentPage
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      
      {/* Add New Sale Modal */}
      {isNewSaleModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsNewSaleModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add New Sale</h2>
              <button
                onClick={() => setIsNewSaleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaleSubmit} className="space-y-6" onClick={closeAllModalDropdowns}>
              {/* Inventory Search */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Add Items to Cart
                  </label>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                    </svg>
                    <span className="text-sm text-green-600 font-medium">Scanner Ready</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={inventorySearchTerm}
                      onChange={(e) => {
                        // Auto-trim input value to handle pasted content with spaces
                        const trimmedValue = e.target.value.trim();
                        setInventorySearchTerm(trimmedValue);
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleInventorySearch())}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Search by Inventory ID, IMEI, Model, or scan barcode..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleInventorySearch}
                    className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors h-10 flex items-center gap-2 whitespace-nowrap"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search
                  </button>
                </div>

                {/* Barcode Scanner - Always Active */}
                <BarcodeScanner
                  onScan={handleBarcodeScan}
                  isActive={isNewSaleModalOpen}
                  className="hidden"
                />
                
                {/* Search Results */}
                {isLoadingInventory && (
                  <div className="mt-3 p-4 text-center">
                    <LoadingSpinner size="sm" text="Loading inventory..." />
                  </div>
                )}
                
                {!isLoadingInventory && searchResults.length > 0 && (
                  <div className="mt-3 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                    <div className="space-y-2 p-2">
                      {searchResults.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 mb-1">{item.phone_model}</p>
                            <p className="text-xs text-gray-500 mb-1">IMEI: {item.imei_mac_address}</p>
                            <p className="text-xs text-gray-400">ID: {item.inventory_id}</p>
                            <p className="text-xs text-gray-400 mt-1">Minimum Sellable Cost: {formatCurrency(typeof item.selling_price === 'string' ? parseFloat(item.selling_price) : item.selling_price || 0)}</p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <span className="absolute left-3 top-2.5 text-sm text-gray-500">{getCurrencySymbol()}</span>
                              <input
                                type="text"
                                placeholder="Price"
                                className="w-36 pl-8 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                const input = e.currentTarget.previousElementSibling?.querySelector('input') as HTMLInputElement;
                                const price = parseFloat(input.value);
                                if (price > 0) {
                                  addToCart(item, price);
                                  input.value = '';
                                }
                              }}
                              className="px-4 py-2.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                            >
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {!isLoadingInventory && searchResults.length === 0 && hasSearched && inventorySearchTerm && (
                  <div className="mt-3 p-3 text-sm text-gray-500 text-center border border-gray-200 rounded-lg">
                    No available inventory items found. Try searching with different keywords.
                  </div>
                )}
              </div>

              {/* Cart Items */}
              {cartItems.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Selected Items ({cartItems.length})</h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {cartItems.map((cartItem) => (
                      <div key={cartItem.inventory_item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        {/* Item Details */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1">{cartItem.inventory_item.phone_model}</p>
                          <p className="text-xs text-gray-500 mb-1">IMEI: {cartItem.inventory_item.imei_mac_address}</p>
                          <p className="text-xs text-gray-400">ID: {cartItem.inventory_item.inventory_id}</p>
                        </div>
                        
                        {/* Selling Price Input */}
                        <div className="flex-shrink-0">
                          <label className="block text-xs text-gray-500 mb-1">Selling Price</label>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-sm text-gray-500">{getCurrencySymbol()}</span>
                            <input
                              type="number"
                              value={cartItem.selling_price}
                              onChange={(e) => updateCartItemPrice(cartItem.inventory_item.id, parseFloat(e.target.value) || 0)}
                              className="w-36 pl-8 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="0"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        
                        {/* Delete Button */}
                        <div className="flex-shrink-0 flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => removeFromCart(cartItem.inventory_item.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove item"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Customer Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={newSaleForm.customer.phone}
                        onChange={(e) => {
                          const phoneValue = e.target.value;
                          const digitsOnly = phoneValue.replace(/\D/g, '');
                          
                          setNewSaleForm(prev => ({
                            ...prev,
                            customer: { ...prev.customer, phone: phoneValue }
                          }));
                          
                          // Trigger lookup when phone number has 10+ digits
                          if (digitsOnly.length >= 10) {
                            lookupCustomerByPhone(phoneValue);
                          } else if (phoneValue.length === 0) {
                            // Reset customer details if phone is cleared
                            setNewSaleForm(prev => ({
                              ...prev,
                              customer: {
                                ...prev.customer,
                                name: '',
                                aadhar: '',
                                address: ''
                              }
                            }));
                          }
                        }}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        placeholder="+91 XXXXX XXXXX"
                        required
                      />
                      {isLoadingCustomer && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <LoadingSpinner size="sm" color="primary" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={newSaleForm.customer.name}
                      onChange={(e) => setNewSaleForm(prev => ({
                        ...prev,
                        customer: { ...prev.customer, name: e.target.value }
                      }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter customer name"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aadhar Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={newSaleForm.customer.aadhar}
                      onChange={(e) => setNewSaleForm(prev => ({
                        ...prev,
                        customer: { ...prev.customer, aadhar: e.target.value }
                      }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="XXXX-XXXX-XXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address (Optional)
                    </label>
                    <input
                      type="text"
                      value={newSaleForm.customer.address}
                      onChange={(e) => setNewSaleForm(prev => ({
                        ...prev,
                        customer: { ...prev.customer, address: e.target.value }
                      }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter customer address"
                    />
                  </div>
                </div>
              </div>
              
              {/* Sale Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method *
                    </label>
                    <div className="relative">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeAllModalDropdowns();
                          setIsModalPaymentMethodDropdownOpen(!isModalPaymentMethodDropdownOpen);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      >
                        <span className="text-sm text-gray-700">{newSaleForm.payment_method}</span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalPaymentMethodDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isModalPaymentMethodDropdownOpen && (
                        <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                          {['cash', 'upi', 'bank_transfer', 'card', 'cheque'].map((option, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNewSaleForm(prev => ({ ...prev, payment_method: option }));
                                setIsModalPaymentMethodDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                newSaleForm.payment_method === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sale Date *
                    </label>
                    <input
                      type="date"
                      value={newSaleForm.sale_date}
                      onChange={(e) => setNewSaleForm(prev => ({ ...prev, sale_date: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branch *
                    </label>
                    <div className="relative">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Don't open dropdown for branch managers
                          if (user?.role === 'branch_manager') return;
                          closeAllModalDropdowns();
                          setIsModalBranchDropdownOpen(!isModalBranchDropdownOpen);
                        }}
                        disabled={user?.role === 'branch_manager'}
                        className={`w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors h-10 ${
                          user?.role === 'branch_manager' 
                            ? 'bg-gray-100 cursor-not-allowed opacity-70' 
                            : 'bg-white hover:border-gray-300'
                        }`}
                      >
                        <span className="text-sm text-gray-700">
                          {newSaleForm.branch_id ? getBranchNameById(newSaleForm.branch_id) : 'Select Branch'}
                          {user?.role === 'branch_manager' && newSaleForm.branch_id && (
                            <span className="text-xs text-gray-500 ml-1">(Your Branch)</span>
                          )}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isModalBranchDropdownOpen && user?.role !== 'branch_manager' && (
                        <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                          {modalBranchOptions.map((option, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Find the branch ID for the selected branch name
                                const selectedBranch = branches.find(branch => branch.name === option);
                                const branchId = selectedBranch ? selectedBranch.id : option;
                                setNewSaleForm(prev => ({ ...prev, branch_id: branchId }));
                                setIsModalBranchDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                getBranchNameById(newSaleForm.branch_id) === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Final Amount Display */}
                {cartItems.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Final Amount to Collect:</span>
                      <span className="text-primary-600">{formatCurrency(newSaleForm.final_amount)}</span>
                    </div>
                  </div>
                )}
              </div>


              {/* Sale Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sale Notes (Optional)
                  <span className="text-xs text-gray-500 font-normal ml-2">• Notes will be displayed in the invoice</span>
                </label>
                <textarea
                  rows={3}
                  value={newSaleForm.notes}
                  onChange={(e) => setNewSaleForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                  placeholder="Add any notes about the sale..."
                ></textarea>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsNewSaleModalOpen(false);
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={cartItems.length === 0 || !newSaleForm.customer.name || !newSaleForm.customer.phone || !newSaleForm.branch_id}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Create Sale ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      
      {/* Invoice Detail Modal */}
      {isInvoiceModalOpen && selectedSale && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsInvoiceModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Print Button */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Sale Details - {selectedSale.invoiceId}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => downloadInvoice().catch(console.error)}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                >
                  <DownloadIcon />
                  Download Invoice
                </button>
                <button
                  onClick={() => setIsInvoiceModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Invoice Content */}
            <div className="space-y-6">
              {/* Customer Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Name</p>
                    <p className="text-sm text-gray-900">{selectedSale.customer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Phone</p>
                    <p className="text-sm text-gray-900">{selectedSale.customer.phone}</p>
                  </div>
                  {selectedSale.customer.address && (
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-600">Address</p>
                      <p className="text-sm text-gray-900">{selectedSale.customer.address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sale Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Sale Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Invoice ID</p>
                    <p className="text-sm font-mono text-gray-900">{selectedSale.invoiceId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Date</p>
                    <p className="text-sm text-gray-900">{new Date(selectedSale.date).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Branch</p>
                    <p className="text-sm text-gray-900">{selectedSale.branch}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Payment Method</p>
                    <p className="text-sm text-gray-900">{selectedSale.paymentMethod}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Status</p>
                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      {selectedSale.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Product Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Information</h3>
                {selectedSale.saleItems && selectedSale.saleItems.length > 1 ? (
                  // Multiple items - show as table
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">This order contains {selectedSale.saleItems.length} items:</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 text-gray-600 font-medium">Device Model</th>
                            <th className="text-left py-2 text-gray-600 font-medium">Category</th>
                            <th className="text-left py-2 text-gray-600 font-medium">IMEI/MAC</th>
                            <th className="text-left py-2 text-gray-600 font-medium">Selling Price</th>
                            <th className="text-left py-2 text-gray-600 font-medium">Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSale.saleItems.map((item, index) => (
                            <tr key={item.id} className="border-b border-gray-100">
                              <td className="py-2 text-gray-900">{item.inventory.phone_model}</td>
                              <td className="py-2">
                                <div className="flex items-center gap-2">
                                  <div className="p-1 bg-gray-100 rounded">
                                    {getCategoryIcon(item.inventory.category.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()))}
                                  </div>
                                  <span className="text-gray-900">{item.inventory.category.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
                                </div>
                              </td>
                              <td className="py-2 font-mono text-gray-900">{item.inventory.imei_mac_address}</td>
                              <td className="py-2 text-gray-900 font-medium">{formatCurrency(item.selling_price)}</td>
                              <td className="py-2 text-green-600 font-medium">{formatCurrency(item.profit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  // Single item - show as grid
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Device Model</p>
                      <p className="text-sm text-gray-900">{selectedSale.deviceModel}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Category</p>
                      <div className="flex items-center gap-2">
                        <div className="p-1 bg-gray-100 rounded">
                          {getCategoryIcon(selectedSale.category)}
                        </div>
                        <span className="text-sm text-gray-900">{selectedSale.category}</span>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-600">IMEI/MAC Address</p>
                      <p className="text-sm font-mono text-gray-900">{selectedSale.imei}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Financial Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Financial Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Buying Cost</p>
                    <p className="text-sm text-gray-900">{formatCurrency(selectedSale.buyingCost)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Expenses</p>
                    <p className="text-sm text-gray-900">{formatCurrency(selectedSale.expenses)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Selling Amount</p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(selectedSale.sellingCost)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Profit</p>
                    <div>
                      <p className="text-sm font-semibold text-green-600">{formatCurrency(selectedSale.profit)}</p>
                      <p className="text-xs text-gray-500">({selectedSale.profitMargin.toFixed(1)}% margin)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedSale.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
                  <p className="text-sm text-gray-900">{selectedSale.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Custom Date Range Modal */}
      {isCustomDateModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Custom Date Range</h3>
              <button
                onClick={() => setIsCustomDateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  min={tempStartDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsCustomDateModalOpen(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (tempStartDate && tempEndDate) {
                    setCustomStartDate(tempStartDate);
                    setCustomEndDate(tempEndDate);
                    setDateRange('Custom');
                    setIsCustomDateRange(true);
                    setCurrentPage(1);
                    setIsCustomDateModalOpen(false);
                  } else {
                    showToast('Please select both start and end dates', 'warning');
                  }
                }}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Apply Range
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      </div>
    </>
  );
};

export default SalesManagement;